import { ListicleItem } from '@/components/Listicle/types';
import { useEffect, useState } from 'react';
import { restRequest } from './restRequest';
import { ContentData } from '@/types';
import * as Sentry from '@sentry/nextjs';
import { BuilderError } from '@/types/errors';

type LawsuitCollectionData = {
	lawsuits: Array<{
		lawsuit: {
			'@type': '@builder.io/core:Reference';
			id: string;
			model: string;
			value: {
				data: {
					defaultImage: string;
					name: string;
					description: string;
					mainUrl: string;
					domain: {
						'@type': '@builder.io/core:Reference';
						id: string;
						model: string;
					};
				};
			};
		};
	}>;
};

export const useLawsuits = (lawsuitCollectionId: string | null) => {
	if (!lawsuitCollectionId) {
		return [];
	}

	const [lawsuits, setLawsuits] = useState<ListicleItem[]>([]); // turn this and the useEffect into a custom hook

	useEffect(() => {
		const fetchLawsuitData = async () => {
			try {
				const data = await restRequest<ContentData<LawsuitCollectionData, 'data'>[]>(
					'lawsuit-collection',
					`id=${lawsuitCollectionId}`,
				);
				if (data?.[0].data?.lawsuits?.length) {
					const lawsuitsData = data[0].data.lawsuits;

					const listicleItems = lawsuitsData.map((lawsuit) => {
						const lawsuitData = lawsuit.lawsuit?.value?.data;
						return {
							heading: `<h4><strong>${lawsuitData.name}</strong></h4>`,
							copy: `<p>${lawsuitData.description || ''}</p>`,
							image: lawsuitData.defaultImage,
							button: {
								buttonText: 'See if I Qualify',
								buttonColor: '#089BAB',
								buttonHref: lawsuitData.mainUrl,
							},
							horizontalRule: false,
							hrColor: '#089BAB',
						};
					});
					setLawsuits(listicleItems);
				} else {
					Sentry.withScope((scope) => {
						scope.setTag('api', 'Builder');
						scope.setLevel('error');
						Sentry.captureException(
							new BuilderError(`No lawsuit data found for collection ${lawsuitCollectionId}`),
						);
					});
				}
			} catch (error) {
				Sentry.captureException(error as Error);
			}
		};

		fetchLawsuitData();
	}, [lawsuitCollectionId]);

	return lawsuits;
};
